<template>
  <div>
    <div
      style="display: flex; justify-content: space-between; align-items: center"
    >
      <h1 v-bg-typography="'h1'" :style="{ marginTop: '10px' }">Insights</h1>
      <bg-button
        @click="
          $router.push(`/research/test/${$route.params.id}/insights-wizard`)
        "
        text="Wizard"
      ></bg-button>
    </div>
    <div
      class="message"
      v-if="
        videoSections.length === 0 && !isLoading && testPlatform === 'video'
      "
    >
      <bg-icon name="alertCircle"></bg-icon>
      <div class="content">
        There are no scenes created yet. Please go to 'Scenes' first.
      </div>
      <bg-button
        @click="$router.push(`/research/test/${$route.params.id}/scenes`)"
        text="To Scenes"
      ></bg-button>
    </div>
    <bg-loader :dark="true" v-if="isLoading"></bg-loader>

    <div v-if="testPlatform !== 'video'">
      <insights-analysis
        ref="insights"
        :isLoading="isLoading"
        style="margin: 30px 0px"
        @change="updateInsights"
        :selectFeedback="selectFeedback"
      />
    </div>
    <div v-else>
      <List
        v-if="!isLoading && videoSections.length > 0"
        ref="list"
        :headers="list.headers"
        :values="list.values"
        :actions="list.actions"
        :data="videoSections"
        :fractions="list.fractions"
        @change="getChanges"
        selectMode="true"
      />
    </div>
    <div
      class="notes"
      v-if="
        !this.isLoading &&
        this.testPlatform === 'video' &&
        this.videoPp &&
        this.videoPp.sections.length > 0 &&
        this.activeIndex != null
      "
    >
      <h2 v-bg-typography="'h2'" :style="{ marginTop: '10px' }">
        Notes for
        <span style="font-weight: 500">{{
          this.videoPp.sections[this.activeIndex].label.name
        }}</span>
      </h2>
      <div
        v-for="(scene, index) in this.videoPp.sections"
        :key="index"
        v-show="index === activeIndex"
      >
        <Editor
          title="Explanation"
          :whiteBackground="true"
          @update="updateExplanation"
          :loadContent="
            scene.consultancy_notes.find(
              (el) => el.note_type === 'explanation' || el.note_type === 1,
            ).note
          "
        >
        </Editor>
        <Editor
          title="Suggestions"
          :whiteBackground="true"
          @update="updateSuggestions"
          :loadContent="
            scene.consultancy_notes.find(
              (el) => el.note_type === 'suggestion' || el.note_type === 2,
            ).note
          "
        >
        </Editor>
        <Editor
          title="Summary"
          :whiteBackground="true"
          @update="updateSummary"
          :loadContent="
            scene.consultancy_notes.find(
              (el) => el.note_type === 'summary' || el.note_type === 0,
            ).note
          "
        >
        </Editor>
      </div>
    </div>
  </div>
</template>

<style scoped>
.message {
  background-color: #636383;
  padding: 30px 30px;
  display: flex;
  gap: 30px;
  border-radius: 6px;
  color: white;
  margin-bottom: 25px;
  align-items: center;
  justify-content: space-between;
}

.message svg {
  min-width: 50px;
}

.notes {
  margin-bottom: 200px;
}

.notes {
  margin-top: 40px;
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgLoader from "@/components/bg-loader";
import InsightsAnalysis from "@/components/insights-analysis";
import API from "@/api";
import List from "@/components/list";
import BgButton from "@/components/bg-button";
import BgIcon from "@/components/bg-icon";
import Editor from "@/components/editor";

export default {
  components: {
    InsightsAnalysis,
    BgLoader,
    List,
    BgButton,
    BgIcon,
    Editor,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      activeIndex: null,
      insights: [],
      isLoading: false,
      list: {
        headers: ["Scene", "Explanation", "Suggestions", "Summary"],
        values: ["name", "explanation", "suggestions", "summary"],
        fractions: [3, 1, 1, 1],
        actions: [],
      },
      processed: false,
      testPlatform: null,
      test: {},
      videoPp: null,
    };
  },
  computed: {
    showMessage: function () {
      if (this.testPlatform === "video" && !this.processed) {
        return true;
      } else {
        return false;
      }
    },
    videoSections: function () {
      let sections = [];
      if (this.videoPp) {
        this.videoPp.sections.forEach((el, index) => {
          if (el.label) {
            sections.push({
              name: el.label.name,
              explanation:
                el.consultancy_notes.find(
                  (el) => el.note_type === "explanation",
                ).note != "" &&
                el.consultancy_notes.find(
                  (el) => el.note_type === "explanation",
                ).note != "<p></p>",
              suggestions:
                el.consultancy_notes.find((el) => el.note_type === "suggestion")
                  .note != "" &&
                el.consultancy_notes.find((el) => el.note_type === "suggestion")
                  .note != "<p></p>",
              summary:
                el.consultancy_notes.find((el) => el.note_type === "summary")
                  .note != "" &&
                el.consultancy_notes.find((el) => el.note_type === "summary")
                  .note != "<p></p>",
              index: index,
            });
          }
        });
      }
      return sections;
    },
  },
  async mounted() {
    this.isLoading = true;
    this.videoPp = null;
    this.test = {};
    this.test = await API.test.getTest(this.$route.params.id);
    if (
      this.test.platform === "video" &&
      this.test.participants.find((el) => el.type === "video_heatmap")
    ) {
      await this.fetchData();
    }
    if (
      this.test.participants &&
      this.test.participants.find((el) => el.type === "video_heatmap")
    ) {
      this.processed = true;
    }
    this.testPlatform = this.test.platform;
    if (this.$route.query.open) {
      const child = this.$refs.insights;
      child.open(Number(this.$route.query.open));
    }

    this.isLoading = false;
  },
  methods: {
    async fetchData() {
      this.isLoading = true;
      this.videoPp = await API.test.getVideoParticipant(this.$route.params.id);
      if (this.videoPp) {
        // Check if any sections need consultancy notes created
        const sectionsNeedingNotes = this.videoPp.sections.filter(
          (sec) => sec.consultancy_notes.length === 0,
        );

        // Only create notes if there are sections that need them
        if (sectionsNeedingNotes.length > 0) {
          // Create notes for each section that needs them
          const promises = sectionsNeedingNotes.map(async (sec) => {
            const newNotes = [
              {
                note_type: 2,
                note: "",
                label_id: sec.label_id,
              },
              {
                note_type: 0,
                note: "",
                label_id: sec.label_id,
              },
              {
                note_type: 1,
                note: "",
                label_id: sec.label_id,
              },
            ];
            sec.consultancy_notes.push(...newNotes);
            return API.test.createConsultancyNotes(newNotes);
          });

          // Wait for all notes to be created
          await Promise.all(promises);

          // Fetch the data ONE MORE TIME to get the created notes with IDs
          this.videoPp = await API.test.getVideoParticipant(
            this.$route.params.id,
          );
        }
      }

      this.isLoading = false;
    },
    getChanges(val) {
      if (val.action === "Select") {
        const child = this.$refs.list;
        child.resetActivated();
        this.activeIndex = val.item.index;
      }
    },
    updateInsights(val) {
      this.insights = val;
    },
    async updateExplanation(val) {
      this.videoPp.sections[this.activeIndex].consultancy_notes.find(
        (el) => el.note_type === "explanation" || el.note_type === 1,
      ).note = val;
      const payload = [
        {
          note_type: 1,
          note: this.videoPp.sections[this.activeIndex].consultancy_notes.find(
            (el) => el.note_type === "explanation" || el.note_type === 1,
          ).note,
          label_id: this.videoPp.sections[this.activeIndex].label_id,
        },
      ];
      const result = await API.test.createConsultancyNotes(payload);
    },
    async updateSuggestions(val) {
      this.videoPp.sections[this.activeIndex].consultancy_notes.find(
        (el) => el.note_type === "suggestion" || el.note_type === 2,
      ).note = val;
      const payload = [
        {
          note_type: 2,
          note: this.videoPp.sections[this.activeIndex].consultancy_notes.find(
            (el) => el.note_type === "suggestion" || el.note_type === 2,
          ).note,
          label_id: this.videoPp.sections[this.activeIndex].label_id,
        },
      ];
      const result = await API.test.createConsultancyNotes(payload);
    },
    async updateSummary(val) {
      this.videoPp.sections[this.activeIndex].consultancy_notes.find(
        (el) => el.note_type === "summary" || el.note_type === 2,
      ).note = val;
      const payload = [
        {
          note_type: 0,
          note: this.videoPp.sections[this.activeIndex].consultancy_notes.find(
            (el) => el.note_type === "summary" || el.note_type === 2,
          ).note,
          label_id: this.videoPp.sections[this.activeIndex].label_id,
        },
      ];
      const result = await API.test.createConsultancyNotes(payload);
    },
    selectFeedback(val) {
      const routeObj = {
        path: `/test/${this.$route.params.id}/participant/${val.participant_id}/recording`,
        query: {
          timestamp: val.timestamp_top,
          sp_id: val.id,
        },
      };
      let routeData = this.$router.resolve(routeObj);
      window.open(routeData.href, "_blank");
    },
  },
};
</script>
